import { useCallback, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../../lib/useApi'
import { useRealtimeRefetch } from '../../lib/realtime'
import { haptics } from '../../lib/haptics'
import { SkeletonRow } from '../UI/Skeleton.jsx'
import { renderMd } from '../../lib/mini-md.jsx'

const CATS = [
  { id: 'macro',   label: 'Macro' },
  { id: 'micro',   label: 'Micro' },
  { id: 'problem', label: 'Problem' },
]

const STATUSES = [
  { id: 'active', label: 'Offen' },
  { id: 'done',   label: 'Erledigt' },
  { id: 'dropped',label: 'Verworfen' },
]

export default function QuestsPanel() {
  const api = useApi()
  const qc  = useQueryClient()
  const [cat, setCat] = useState('macro')
  const [statusFilter, setStatusFilter] = useState('active')
  const [text, setText] = useState('')
  const [dragId, setDragId] = useState(null)
  const [selected, setSelected] = useState(new Set())

  const { data: list = [], isLoading } = useQuery({
    queryKey: ['quests', statusFilter],
    queryFn: () => api.get(`/quests?status=${statusFilter}`),
  })

  const refetch = useCallback(() => qc.invalidateQueries({ queryKey: ['quests'] }), [qc])
  useRealtimeRefetch('quests', refetch)

  const create = useMutation({
    mutationFn: (q) => api.post('/quests', q),
    onSuccess: () => { refetch(); haptics.ok() },
  })
  const patch = useMutation({
    mutationFn: ({ id, body }) => api.patch(`/quests/${id}`, body),
    onSuccess: refetch,
  })
  const remove = useMutation({
    mutationFn: (id) => api.del(`/quests/${id}`),
    onSuccess: refetch,
  })
  const reorder = useMutation({
    mutationFn: (items) => api.post('/quests/reorder', items),
    onSuccess: refetch,
  })

  const submit = () => {
    if (!text.trim()) return
    create.mutate({ category: cat, text: text.trim() })
    setText('')
  }

  function toggleSel(id) {
    setSelected((s) => {
      const next = new Set(s)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  async function bulkAction(action) {
    haptics.bump()
    const ids = [...selected]
    for (const id of ids) {
      if (action === 'done')  await patch.mutateAsync({ id, body: { status: 'done' } }).catch(() => {})
      if (action === 'drop')  await patch.mutateAsync({ id, body: { status: 'dropped' } }).catch(() => {})
      if (action === 'delete')await remove.mutateAsync(id).catch(() => {})
    }
    setSelected(new Set())
  }

  function onDragStart(id) { setDragId(id); haptics.tap() }
  function onDragOver(e) { e.preventDefault() }
  function onDropOn(targetId) {
    if (!dragId || dragId === targetId) { setDragId(null); return }
    const rows = list.filter((q) => q.category === currentDragCat(dragId, list))
    const fromIdx = rows.findIndex((r) => r.id === dragId)
    const toIdx   = rows.findIndex((r) => r.id === targetId)
    if (fromIdx < 0 || toIdx < 0) { setDragId(null); return }
    const next = [...rows]
    const [moved] = next.splice(fromIdx, 1)
    next.splice(toIdx, 0, moved)
    const payload = next.map((r, i) => ({ id: r.id, position: i }))
    reorder.mutate(payload)
    haptics.bump()
    setDragId(null)
  }

  return (
    <section className="space-y-3">
      <div className="section-h">Quests</div>

      <div className="border border-card bg-card rounded-themed p-4 space-y-4">
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            {CATS.map((c) => (
              <button key={c.id} onClick={() => setCat(c.id)}
                      className={`tap px-3 py-1.5 rounded-full text-xs font-display tracking-widest uppercase border ${
                        cat === c.id ? 'border-[var(--accent)] text-accent bg-[var(--accent)]/10' : 'border-card opacity-70 hover:opacity-100'
                      }`}>
                {c.label}
              </button>
            ))}
          </div>

          <div className="flex gap-2">
            <input value={text} onChange={(e) => setText(e.target.value)}
                   onKeyDown={(e) => e.key === 'Enter' && submit()}
                   placeholder={`Neue ${CATS.find((c) => c.id === cat)?.label}-Quest…  (markdown ok)`}
                   className="flex-1 bg-transparent border border-card rounded-themed px-3 py-2 text-sm focus:border-[var(--accent)] outline-none" />
            <button onClick={submit} className="px-4 py-2 rounded-themed bg-accent font-semibold tap text-sm">+</button>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex gap-1">
            {STATUSES.map((s) => (
              <button key={s.id} onClick={() => { setStatusFilter(s.id); setSelected(new Set()) }}
                      className={`text-[10px] px-2 py-1 rounded-themed border ${
                        statusFilter === s.id ? 'border-[var(--accent)] text-accent' : 'border-card opacity-60'
                      }`}>
                {s.label}
              </button>
            ))}
          </div>
          {selected.size > 0 && (
            <div className="flex gap-2 text-xs">
              <span className="opacity-60 num">{selected.size} ausgewählt</span>
              <button onClick={() => bulkAction('done')}  className="px-2 py-1 rounded-themed border border-[var(--accent)]/40 text-accent">✓ done</button>
              <button onClick={() => bulkAction('drop')}  className="px-2 py-1 rounded-themed border border-yellow-400/40 text-yellow-400">drop</button>
              <button onClick={() => bulkAction('delete')} className="px-2 py-1 rounded-themed border border-red-400/40 text-red-400">×</button>
            </div>
          )}
        </div>

        {isLoading ? (
          <SkeletonRow count={3} />
        ) : (
          CATS.map((c) => {
            const rows = list.filter((q) => q.category === c.id).sort((a, b) => a.position - b.position)
            if (rows.length === 0) return null
            return (
              <div key={c.id} className="space-y-1">
                <div className="label-mini">{c.label}</div>
                <ul className="divide-y divide-card">
                  {rows.map((q) => (
                    <li key={q.id}
                        draggable={statusFilter === 'active'}
                        onDragStart={() => onDragStart(q.id)}
                        onDragOver={onDragOver}
                        onDrop={() => onDropOn(q.id)}
                        className={`py-2 flex items-center gap-3 group ${
                          dragId === q.id ? 'opacity-40' : ''
                        } ${statusFilter === 'active' ? 'cursor-grab active:cursor-grabbing' : ''}`}>
                      <input type="checkbox" checked={selected.has(q.id)} onChange={() => toggleSel(q.id)}
                             className="accent-[var(--accent)] tap" />
                      {statusFilter === 'active' && (
                        <button onClick={() => patch.mutate({ id: q.id, body: { status: 'done' } })}
                                className="tap w-8 h-8 grid place-items-center border border-card rounded-full hover:border-[var(--accent)]">
                          ○
                        </button>
                      )}
                      <div className={`flex-1 text-sm space-y-1 ${q.status !== 'active' ? 'opacity-50 line-through' : ''}`}>
                        {renderMd(q.text)}
                      </div>
                      {statusFilter === 'active' && (
                        <button onClick={() => patch.mutate({ id: q.id, body: { status: 'dropped' } })}
                                className="opacity-30 group-hover:opacity-100 hover:text-yellow-400 text-xs">drop</button>
                      )}
                      {statusFilter !== 'active' && (
                        <button onClick={() => patch.mutate({ id: q.id, body: { status: 'active' } })}
                                className="opacity-30 group-hover:opacity-100 hover:text-accent text-xs">restore</button>
                      )}
                      <button onClick={() => remove.mutate(q.id)}
                              className="opacity-30 group-hover:opacity-100 hover:text-red-400">×</button>
                    </li>
                  ))}
                </ul>
              </div>
            )
          })
        )}

        {!isLoading && list.length === 0 && (
          <div className="text-sm opacity-60 text-center py-4">
            {statusFilter === 'active' ? 'Noch keine offenen Quests.' :
             statusFilter === 'done'   ? 'Noch keine erledigten Quests.' :
                                         'Keine verworfenen Quests.'}
          </div>
        )}
      </div>
    </section>
  )
}

function currentDragCat(id, list) {
  return list.find((q) => q.id === id)?.category
}
